import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useControllableState } from '../../hooks/useControllableState.js';
import {
  hex2rgb,
  hsl2hsv,
  hsv2hsl,
  hsv2rgb,
  isLightRgb,
  rgb2css,
  rgb2hex,
  rgb2hsv,
  type HSL,
  type HSV,
  type RGB,
} from './colorMath.js';
import { AlphaSlider, EyedropperButton, HueSlider, SVPlane } from './ColorPickerControls.js';
import { ColorPickerInputs, type ColorPickerFormat } from './ColorPickerInputs.js';

export type ColorPickerSize = 'sm' | 'md' | 'lg';
export type { ColorPickerFormat } from './ColorPickerInputs.js';

export interface ColorPickerProps {
  /** CSS colour value. Hex/rgb/hsl are editable; CSS variables are resolved for display. */
  value?: string;
  defaultValue?: string;
  onChange?: (color: string) => void;
  /** Array of preset colours rendered below the editor. Default: a curated
   *  10-swatch editorial palette. Pass `[]` to hide the row entirely. */
  swatches?: ReadonlyArray<string>;
  /** Show the numeric input row. Default true. */
  showHex?: boolean;
  /** Show the alpha slider + 8-digit hex output. Default false (full
   *  opacity). The output `value` will use 8-digit hex when alpha < 1. */
  alpha?: boolean;
  /** @deprecated Native `<input type=color>` was removed in favour of the
   *  fully-custom plane. Kept on the type for backwards compatibility. */
  showEyedropper?: boolean;
  size?: ColorPickerSize;
  /** Initial input format. Default `'hex'`. The user can switch via the
   *  inline tab strip. */
  defaultFormat?: ColorPickerFormat;
  /** Emits the original swatch value instead of the resolved hex value.
   *  Useful for theme tokens such as `var(--vq-highlight-yellow-bg)`,
   *  where the saved value should keep adapting when the theme changes. */
  preserveSwatchValue?: boolean;
  /** Element whose cascade is used to resolve CSS variables for the readout. */
  resolveElement?: HTMLElement | null;
  /** When true, every interactive element inside the picker calls
   *  `preventDefault()` on `mousedown`. This keeps focus on whatever
   *  *opened* the picker — typically a contenteditable in an editor —
   *  so the underlying selection is preserved between picks. Default
   *  false (a click moves focus to the swatch button as usual). */
  preserveFocus?: boolean;
  /** Optional "Reset to default" affordance. When provided, a small
   *  ghost button is rendered above the swatch row. Use cases:
   *  - In an editor's text-colour / highlight pickers, the host wires
   *    this to dispatch a clear-formatting command (so the selection
   *    returns to the document's theme-driven default colour rather
   *    than getting painted over with the palette's first swatch).
   *  - In standalone forms, the host can revert to a stored default.
   *  Click semantics: the picker fires the callback but does NOT
   *  close itself — the consumer decides whether to close. */
  onReset?: () => void;
  /** Label rendered on the reset button. Defaults to "Reset". */
  resetLabel?: string;
  /** Label announced to AT. */
  'aria-label'?: string;
  className?: string;
}

const DEFAULT_SWATCHES: ReadonlyArray<string> = [
  '#1a1814',
  '#b43a3a',
  '#d97706',
  '#3f7d41',
  '#0f7fbf',
  '#4b3ea8',
  '#8a5632',
  '#555961',
  '#9a9ba0',
  '#ffffff',
];

function parseInitial(input: string | undefined, resolveElement?: HTMLElement | null): HSV {
  if (input === undefined) return { h: 0, s: 0, v: 0, a: 1 };
  const rgb = hex2rgb(input, resolveElement);
  if (rgb === null) return { h: 0, s: 0, v: 0, a: 1 };
  return rgb2hsv(rgb);
}

function containsCssVariable(value: string | undefined): boolean {
  return value !== undefined && /var\s*\(/i.test(value);
}

function cssVariableTargets(resolveElement: HTMLElement | null | undefined): HTMLElement[] {
  if (typeof document === 'undefined') return [];
  const seen = new Set<HTMLElement>();
  const targets: HTMLElement[] = [];
  const add = (el: HTMLElement | null | undefined): void => {
    if (el === null || el === undefined || seen.has(el)) return;
    seen.add(el);
    targets.push(el);
  };

  add(document.documentElement);
  add(document.body);
  let node: HTMLElement | null =
    resolveElement instanceof HTMLElement && resolveElement.isConnected ? resolveElement : null;
  while (node !== null) {
    add(node);
    node = node.parentElement;
  }

  return targets;
}

/** Custom HSV colour editor. Spec 15 §10. Editorial paper feel:
 *  generous SV plane on top, refined hue + alpha sliders with crafted
 *  thumbs, eyedropper (when supported) sits inline with the sliders,
 *  preview chip + format-switchable inputs, swatch row at the bottom.
 *
 *  Pointer interaction: click + drag inside the plane / sliders adjusts
 *  the value; the drag is captured at the window level so the user can
 *  leave the track without losing focus. Keyboard: arrows nudge by 1%
 *  (10% with Shift); Home/End jump to the extremes; PgUp/PgDn flip value
 *  to the top/bottom of the plane. */
export function ColorPicker(props: ColorPickerProps): ReactNode {
  const {
    value,
    defaultValue,
    onChange,
    swatches = DEFAULT_SWATCHES,
    showHex = true,
    alpha = false,
    size = 'md',
    defaultFormat = 'hex',
    preserveSwatchValue = false,
    resolveElement,
    preserveFocus = false,
    onReset,
    resetLabel = 'Reset',
    className,
  } = props;

  const [, setHex] = useControllableState<string>({
    defaultValue: defaultValue ?? '#000000',
    ...(value !== undefined ? { value } : {}),
    ...(onChange !== undefined ? { onChange } : {}),
  });

  const [hsv, setHsv] = useState<HSV>(() =>
    parseInitial(value ?? defaultValue ?? '#000000', resolveElement),
  );
  const [cssVariableRevision, setCssVariableRevision] = useState(0);
  // Track the hex string that produced the current internal HSV — when
  // the controlled prop arrives equal to what we last emitted we skip
  // the round-trip rgb→hsv (which would clobber hue at S=0).
  const lastEmittedRef = useRef<string>('');

  useEffect(() => {
    if (value === undefined) return;
    const usesDynamicCss = containsCssVariable(value);
    if (!usesDynamicCss && value === lastEmittedRef.current) return;
    const next = parseInitial(value, resolveElement);
    setHsv((prev) => {
      // Keep hue if the incoming colour is achromatic — saves the dot
      // jumping back to red whenever a caller sets pure black/white.
      if (next.s === 0) return { ...next, h: prev.h };
      return next;
    });
  }, [value, resolveElement, cssVariableRevision]);

  useEffect(() => {
    const hasDynamicValue = containsCssVariable(value) || swatches.some(containsCssVariable);
    if (
      !hasDynamicValue ||
      typeof MutationObserver === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return undefined;
    }

    const observer = new MutationObserver(() => {
      setCssVariableRevision((revision) => revision + 1);
    });
    for (const target of cssVariableTargets(resolveElement)) {
      observer.observe(target, { attributes: true });
    }

    return () => {
      observer.disconnect();
    };
  }, [value, swatches, resolveElement]);

  const rgb = useMemo<RGB>(() => hsv2rgb(hsv), [hsv]);
  const hsl = useMemo<HSL>(() => hsv2hsl(hsv), [hsv]);
  const displayHex = useMemo(() => rgb2hex(rgb), [rgb]);

  const commit = useCallback(
    (next: HSV, emittedValue?: string) => {
      setHsv(next);
      const nextRgb = hsv2rgb(next);
      const nextHex = rgb2hex(nextRgb);
      const nextValue = emittedValue ?? nextHex;
      lastEmittedRef.current = nextValue;
      setHex(nextValue);
    },
    [setHex],
  );

  const onSV = useCallback(
    (s: number, v: number) => {
      commit({ ...hsv, s, v });
    },
    [hsv, commit],
  );
  const onHue = useCallback(
    (h: number) => {
      commit({ ...hsv, h });
    },
    [hsv, commit],
  );
  const onAlpha = useCallback(
    (a: number) => {
      commit({ ...hsv, a });
    },
    [hsv, commit],
  );

  const pickHexExternal = useCallback(
    (s: string) => {
      const parsed = hex2rgb(s, resolveElement);
      if (parsed === null) return;
      const next = rgb2hsv(parsed);
      commit({ ...next, a: alpha ? next.a : 1 });
    },
    [commit, alpha, resolveElement],
  );

  const pickSwatch = useCallback(
    (s: string) => {
      const parsed = hex2rgb(s, resolveElement);
      if (parsed === null) return;
      const next = rgb2hsv(parsed);
      // Swatches are typically opaque — preserve current alpha so users
      // who set 50% don't lose it when they tap a preset.
      commit({ ...next, a: alpha ? hsv.a : 1 }, preserveSwatchValue && !alpha ? s : undefined);
    },
    [commit, alpha, hsv.a, preserveSwatchValue, resolveElement],
  );

  const classes = ['vq-color-picker', `vq-color-picker--${size}`, className]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      role="group"
      aria-label={props['aria-label'] ?? 'Colour picker'}
      data-component="ColorPicker"
    >
      <SVPlane hue={hsv.h} s={hsv.s} v={hsv.v} onChange={onSV} />

      <div className="vq-color-picker__sliders" data-slot="sliders">
        <div className="vq-color-picker__slider-stack" data-slot="slider-stack">
          <HueSlider hue={hsv.h} onChange={onHue} />
          {alpha && <AlphaSlider rgb={rgb} alpha={hsv.a} onChange={onAlpha} />}
        </div>
        <EyedropperButton onPick={pickHexExternal} />
      </div>

      {showHex && (
        <div className="vq-color-picker__readout" data-slot="readout">
          <span
            className="vq-color-picker__preview-chip"
            aria-hidden="true"
            style={
              {
                '--vq-cp-preview': rgb2css(rgb),
              } as React.CSSProperties
            }
          />
          <ColorPickerInputs
            hex={displayHex}
            rgb={rgb}
            hsl={hsl}
            alpha={alpha}
            defaultFormat={defaultFormat}
            onCommitHex={(s) => {
              const parsed = hex2rgb(s, resolveElement);
              if (parsed === null) return;
              const next = rgb2hsv(parsed);
              commit({ ...next, a: alpha ? next.a : 1 });
            }}
            onCommitRgb={(next) => {
              const h = rgb2hsv(next);
              commit({ ...h, a: alpha ? next.a : 1 });
            }}
            onCommitHsl={(next) => {
              const v = hsl2hsv(next);
              commit({ ...v, a: alpha ? next.a : 1 });
            }}
          />
        </div>
      )}

      {onReset !== undefined && (
        <button
          type="button"
          className="vq-color-picker__reset"
          data-slot="reset"
          onMouseDown={
            preserveFocus
              ? (e) => {
                  /* Same focus-preservation contract as swatches —
                   * keeps the contenteditable selection alive so the
                   * host's clear-formatting command has a target. */
                  e.preventDefault();
                }
              : undefined
          }
          onClick={() => {
            onReset();
          }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
            focusable="false"
          >
            {/* Counter-clockwise arrow — universal "reset / undo" glyph. */}
            <path
              d="M3 4.5h3.5a3 3 0 0 1 0 6H4"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
            <polyline
              points="4.5 2.5 2.5 4.5 4.5 6.5"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
          <span>{resetLabel}</span>
        </button>
      )}

      {swatches.length > 0 && (
        <div className="vq-color-picker__swatches" role="radiogroup" aria-label="Preset colours">
          {swatches.map((s) => {
            const parsed = hex2rgb(s, resolveElement);
            const sel =
              parsed !== null && rgb2hex({ ...parsed, a: 1 }) === rgb2hex({ ...rgb, a: 1 });
            const label = parsed !== null ? rgb2css(parsed) : 'Custom colour';
            return (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={sel}
                aria-label={label}
                className="vq-color-picker__swatch"
                style={{ background: s }}
                onMouseDown={
                  preserveFocus
                    ? (e) => {
                        // Keep focus on the host that opened the picker
                        // (a contenteditable, typically) so the text
                        // selection is preserved across swatch picks.
                        e.preventDefault();
                      }
                    : undefined
                }
                onClick={() => {
                  pickSwatch(s);
                }}
                data-selected={sel ? 'true' : 'false'}
                data-light={parsed !== null && isLightRgb(parsed) ? 'true' : 'false'}
              >
                {sel && (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                    <polyline
                      points="2 5 4 7 8 3"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
